import { useEffect } from 'react'
import { View } from 'react-native'
import Animated, {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'
import { Image } from 'expo-image'
import { ImageAny } from '@ui/utils/image'
import Typography from '@ui/typography'
import { colors } from '@ui/colors'
import { AnimatedPressable } from '@ui/animated/nativewindReanimated'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import Border from '@ui/effects/Border'

import { height, imageSize, width } from './styles'

interface Props {
  text: string
  image?: ImageAny
  onPress?: () => void
  isActive?: boolean
  hideBorder?: boolean
}

const getActiveValue = (isActive: boolean | undefined) => (isActive ? 1 : 0)

export default function PackageAttribute({
  text,
  image,
  onPress,
  isActive,
  hideBorder = true,
}: Props) {
  const animateActive = useSharedValue(getActiveValue(isActive))
  const { animation, onPressIn, onPressOut } = useAnimatedPress()

  const stylePressedImage = useAnimatedStyle(() => {
    return {
      transform: [{ scale: interpolate(animation.value, [0, 1], [1, 1.25]) }],
    }
  })

  const styleActiveBorder = useAnimatedStyle(() => {
    return {
      borderWidth: interpolate(animateActive.value, [0, 1], [1, 2]),
      borderColor: interpolateColor(
        animateActive.value,
        [0, 1],
        [colors.text0, colors.text]
      ),
    }
  })

  useEffect(() => {
    animateActive.set(withSpring(getActiveValue(isActive)))
  }, [isActive])

  return (
    <AnimatedPressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
    >
      {!hideBorder && (
        <Border style={styleActiveBorder} className="b-0 rounded-xl" />
      )}
      <View
        className="flex flex-col items-center justify-start gap-3 rounded-lg py-4"
        style={[{ width, height }]}
      >
        <Animated.View
          className="h-full w-full"
          style={[{ width: imageSize, height: imageSize }, stylePressedImage]}
        >
          {image ? (
            <Image
              source={image}
              contentFit="contain"
              cachePolicy="memory-disk"
              contentPosition="center"
              transition={300}
              style={{ flex: 1 }}
            />
          ) : (
            <View
              style={{
                width: imageSize,
                height: imageSize,
                backgroundColor: colors.black10,
                borderRadius: imageSize,
              }}
            />
          )}
        </Animated.View>
        <View
          className="flex flex-col items-center justify-center overflow-hidden px-2"
          style={{ width, height: 32 }}
        >
          <Typography
            variant="XS"
            className="wrap text-center align-middle leading-tight"
            numberOfLines={2}
          >
            {text}
          </Typography>
        </View>
      </View>
    </AnimatedPressable>
  )
}
